import { Component, OnDestroy, OnInit } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { filter, tap } from 'rxjs/operators';
import { EstimateType, RevenueAreaType } from '../../../client-api.service';
import { NumberParsingUtil } from '../../../shared/number-parsing-util';
import { tapError } from '../../../shared/tap-error-operator';
import { EstimateInputDebounceManager } from '../../estimate-input-debounce-manager';
import { AreaEstimates } from '../../models/area-estimates';
import { ExtendedRevenueAreaType } from '../../models/area-type';
import { RevenueStateService, RootAreaState } from '../../revenue-state.service';
import { SubsManager } from 'src/app/shared/tcc-ui/subs-manager';
import { NotificationsService } from 'src/app/shared/tcc-ui/notifications';
import { AggregateUtil } from 'src/app/shared/aggregate-util';

@Component({
  selector: 'app-bedroom-count-types',
  templateUrl: './bedroom-count-types.component.html',
  styles: []
})
export class BedroomCountTypesComponent implements OnDestroy, OnInit {
  private allAreaEstimates: AreaEstimates[] | undefined;
  private allUnitTypes: AreaEstimates[] | undefined;
  private allNumberOfBedroomsTypes: AreaEstimates[] | undefined;
  private filterChangeSubj = new BehaviorSubject<string>('');
  private subsMgr = new SubsManager();

  readonly estimateTypeEnumRef = EstimateType;
  readonly isReadOnly$ = this.revState.isReadOnly$;
  readonly tgtPeriodIndex = this.revState.periodSettings.cyJan.index;
  readonly inputDebounceMgr = new EstimateInputDebounceManager(this.revState.periodSettings);

  /** provides access to fitler changes */
  set filter(value: string) {
    this.filterChangeSubj.next(value);
  }
  get filter() {
    return this.filterChangeSubj.value;
  }

  /** The maximum number of consolidated amenities a unit type has. */
  maxUtAmenities: number | undefined;

  /** consolidated amenities mapped to unit type revareaids */
  utAmenityMap: Map<number, AreaEstimates[]> | undefined;

  /** bedrooms count mapped to unit type with bedroom counts */
  bedroomsCountMap: Map<number, AreaEstimates[]> | undefined;

  /** all unit types filtered by fitlerChangeSubj */
  filteredUnitTypes: AreaEstimates[] | undefined;
  filteredNumberOfBedroomsTypes: AreaEstimates[] | undefined;

  constructor(private notifySvc: NotificationsService, private revState: RevenueStateService) { }

  ngOnInit() {
    this.inputDebounceMgr.defaultAppliedOn = this.revState.periodSettings.cyJan.value;

    this.subsMgr.addSub = this.filterChangeSubj.subscribe(() => this.applyFilter());

    this.subsMgr.addSub = this.revState.areas$.pipe(
      filter((x): x is RootAreaState => !!x),
      tap(({ typeMap, utConsolidatedAmenityMap }) => {
        const self = typeMap.get(RevenueAreaType.Self)?.[0];
        if (!self) {
          this.allUnitTypes = [];
          this.allNumberOfBedroomsTypes = [];
          this.utAmenityMap = new Map();
          this.bedroomsCountMap = new Map();
          this.applyFilter();
          return;
        }

        this.allAreaEstimates = Array.from(typeMap.values()).reduce((acc, areaEstimates) => acc.concat(areaEstimates), []);
        this.allUnitTypes = typeMap.get(RevenueAreaType.UnitType) || [];
        this.allNumberOfBedroomsTypes = typeMap.get(RevenueAreaType.NumberOfBedrooms) || [];
        this.utAmenityMap = this.createNumberOfBedroomsAmenityMap(this.allNumberOfBedroomsTypes, utConsolidatedAmenityMap);
        this.maxUtAmenities = Math.max(0, ...Array.from(this.utAmenityMap!.values()).map(x => x.length));
        this.applyFilter();
      })
    ).subscribe();

    // this stream saves changes
    this.subsMgr.addSub = this.inputDebounceMgr.estimateChange$.pipe(
      tap(x => this.revState.enqueueEstimateSave(x.value.revAreaId!, {
        appliedOn: x.value.appliedOn!,
        estimateId: 0,
        estimateType: x.value.estimateType!,
        value: x.value.value
      })),
      tapError(() => this.notifySvc.addError('Unable to save estimates.  Please refresh and try again.'))
    ).subscribe();
  }

  ngOnDestroy() {
    this.subsMgr.onDestroy();
  }


  getUnitCountsForNumberOfBedroomsCount(area: AreaEstimates): number | undefined {
    const unitCounts = area.children?.reduce((acc, ut) => acc + (ut.children?.length || 0), 0);
    return unitCounts;
  }
  /** Warning if there is no fpRates */
  hasRateWarning(area: AreaEstimates) {
    return !area.summary!.estBaseFpRate || area.summary!.estBaseFpRate < 0;
  }

  /** Warning if there is no estimated renewal rate but there are renewal leases. */
  harRenewalWarning(area: AreaEstimates) {
    const estRenewalRate = area.periods[this.revState.periodSettings.cyJan.index].aggs.avg?.estRenewalRate;
    return !estRenewalRate && area.periods.some(x => x.estRenewalLeases! > 0);
  }

  isnumberOfBedroomsTypeAreaType(area: AreaEstimates): boolean {
    return area.areaType === RevenueAreaType.NumberOfBedrooms;
  }

  isSelected(area: AreaEstimates) {
    return this.revState.selectedIds.has(area.revAreaId);
  }

  toggleSelectedUnitType(revAreaId: number) {
    this.revState.selectedIds.has(revAreaId)
      ? this.revState.selectedIds.clear()
      : this.revState.selectedIds.set(revAreaId);
  }

  toggleSelectedNumberOfBedroomsCount(revAreaId: number) {
    this.revState.selectedIds.has(revAreaId)
      ? this.revState.selectedIds.clear()
      : this.revState.selectedIds.set(revAreaId);
  }

  updateAggAmenityEstimates(utRevAreaId: number, aggAmenId: number, estimateType: EstimateType, valueRaw: string) {
    try {
      const aggAmenity = this.utAmenityMap?.get(utRevAreaId)?.find(x => x.revAreaId === aggAmenId);
      for (const amenity of aggAmenity!.children!) {
        this.inputDebounceMgr.updateRawEstimate(valueRaw, { revAreaId: amenity.revAreaId, estimateType }, { forCy: true });
      }
    }
    catch {
      this.notifySvc.addError(`Invalid value for estimate: ${valueRaw}.  Nothing was saved.`);
    }
  }

  updateAggAmenityForce(utRevAreaId: number, aggAmenId: number, estimateType: EstimateType) {
    const aggAmenity = this.utAmenityMap!.get(utRevAreaId)!.find(x => x.revAreaId === aggAmenId);
    for (const amenity of aggAmenity!.children!) {
      this.inputDebounceMgr.updateForce({ revAreaId: amenity.revAreaId, estimateType }, true);
    }
  }

  updateEstimate(revAreaId: number, estimateType: EstimateType, valueRaw: string) {
    try {
      this.inputDebounceMgr.updateRawEstimate(valueRaw, { revAreaId, estimateType }, { forCy: true });
    }
    catch {
      this.notifySvc.addError(`Invalid value for estimate: ${valueRaw}.  Nothing was saved.`);
    }
  }

  updateEstimateForce(revAreaId: number, estimateType: EstimateType) {
    this.inputDebounceMgr.updateForce({ revAreaId, estimateType }, true);
  }

  updateUnitCountPctEstimate(revAreaId: number, estimateType: EstimateType, valueRaw: string) {
    try {
      let pctVal = NumberParsingUtil.parseFloat(valueRaw, { normalizePercent: true });
      if (isNaN(pctVal!)) {
        throw new Error();
      }
      if (pctVal! > 1) {
        pctVal = pctVal! / 100;
      }

      const bedroomCountArea = this.allNumberOfBedroomsTypes?.find(x => x.revAreaId === revAreaId);

      if (!bedroomCountArea) {
        throw new Error();
      }

      for (const unitTypeArea of bedroomCountArea.children || []) {
        for (let pIdx = this.tgtPeriodIndex; pIdx < this.revState.periodSettings.periods.length; pIdx++) {
          const appliedOn = this.revState.periodSettings.periods[pIdx].value;
          const unitCount = unitTypeArea.periods[pIdx].unitCount || 0;
          const value = +(pctVal! * unitCount).toFixed(2);

          this.inputDebounceMgr.updateEstimate(value, {
            revAreaId: unitTypeArea.revAreaId,
            estimateType,
            appliedOn
          });
        }
      }
    }
    catch {
      this.notifySvc.addError(`Invalid value for estimate: ${valueRaw}.  Nothing was saved.`);
    }
  }

  /** Applies current value from filterChangeSubj to allUnitTypes and sets unitTypes with it. */
  private applyFilter() {
    const filterText = (this.filterChangeSubj.value || '').trim().toLowerCase();
    this.filteredUnitTypes = (this.allUnitTypes || [])
      .filter(x => (x.displayName || '').toLowerCase().indexOf(filterText) !== -1
        || (x.name || '').toLowerCase().indexOf(filterText) !== -1);


    this.filteredNumberOfBedroomsTypes = (this.allNumberOfBedroomsTypes || [])
      .filter(x => (x.displayName || '').toLowerCase().indexOf(filterText) !== -1
        || (x.name || '').toLowerCase().indexOf(filterText) !== -1);
  }

  private createNumberOfBedroomsAmenityMap(
    numberOfBedroomsTypesAreas: AreaEstimates[],
    unitTypeAmenityMap: Map<number, AreaEstimates[]>
  ) {
    const numberOfBedroomsAmenityMap = new Map<number, AreaEstimates[]>();

    for (const numberOfBedroomsTypeArea of numberOfBedroomsTypesAreas) {
      const amenitiesByName = new Map<string, AreaEstimates>();

      for (const unitType of numberOfBedroomsTypeArea.children || []) {
        for (const unitTypeAmenity of unitTypeAmenityMap.get(unitType.revAreaId) || []) {
          let numberOfBedroomsAmenity = amenitiesByName.get(unitTypeAmenity.name);
          if (!numberOfBedroomsAmenity) {
            numberOfBedroomsAmenity = {
              areaType: ExtendedRevenueAreaType.ConsolidatedUnitTypeAmenity,
              children: [],
              displayName: unitTypeAmenity.displayName,
              name: unitTypeAmenity.name,
              parentRevAreaId: numberOfBedroomsTypeArea.revAreaId,
              periods: this.revState.periodSettings.periods.map((_, i) => ({ monthId: i, aggs: {} })),
              revAreaId: numberOfBedroomsTypeArea.revAreaId * 16777216 + unitTypeAmenity.revAreaId,
              summary: { monthId: -1, aggs: {} }
            };
            amenitiesByName.set(unitTypeAmenity.name, numberOfBedroomsAmenity);
          }

          numberOfBedroomsAmenity.children?.push(...unitTypeAmenity.children || []);
        }
      }

      const amenities = Array.from(amenitiesByName.values())
        .sort((a, b) => a.displayName.localeCompare(b.displayName));

      for (const amenity of amenities) {
        this.setNumberOfBedroomsAmenityCalcs(amenity);
      }

      if (amenities.length > 0) {
        numberOfBedroomsAmenityMap.set(numberOfBedroomsTypeArea.revAreaId, amenities);
      }
    }

    return numberOfBedroomsAmenityMap;
  }

  private setNumberOfBedroomsAmenityCalcs(amenity: AreaEstimates) {
    for (let periodIdx = 0; periodIdx < this.revState.periodSettings.periods.length; periodIdx++) {
      const childPeriodEstimates = amenity.children?.map(x => x.periods[periodIdx]) || [];
      amenity.periods[periodIdx].estNewRate = AggregateUtil.avg(childPeriodEstimates.map(x => x.estNewRate), { skipBlanks: true });
      amenity.periods[periodIdx].origFpRate = AggregateUtil.avg(childPeriodEstimates.map(x => x.origFpRate), { skipBlanks: true });
      amenity.periods[periodIdx].unitCount = childPeriodEstimates.length;
    }

    const cyPeriods = amenity.periods.slice(this.revState.periodSettings.cyJan.index);
    amenity.summary = {
      ...amenity.summary!,
      estNewRate: AggregateUtil.avg(cyPeriods.map(x => x.estNewRate), { skipBlanks: true }),
      origFpRate: AggregateUtil.avg(cyPeriods.map(x => x.origFpRate), { skipBlanks: true }),
      unitCount: AggregateUtil.avg(cyPeriods.map(x => x.unitCount), { skipBlanks: true })
    };
  }

  generateAmenityTooltip(amenityDisplayName: string, unitType: AreaEstimates) {
    let amenityTooltip = '';
    const unitsWithAmenity = [];
    const unitsWithoutAmenity = [];
    const units = this.isnumberOfBedroomsTypeAreaType(unitType)
      ? unitType.children?.flatMap(x => x.children || [])
      : unitType.children;
    amenityTooltip += amenityDisplayName + units?.length;
    if (units) {
      for (const unit of units) {
        let unitHasAmenity = false;
        if (unit.children) {
          for (const amenity of unit.children) {
            if (amenity.displayName == amenityDisplayName) {
              unitHasAmenity = true;
            }
          }
        }
        unitHasAmenity ? unitsWithAmenity.push(unit.displayName) : unitsWithoutAmenity.push(unit.displayName);
      }
    }
    amenityTooltip += '<b>Units with ' + amenityDisplayName + '</b>'
      + '<small style="padding:3px; color:navy;"><b>(' + unitsWithAmenity.length + ' total)</b></small><br>'
      + unitsWithAmenity + '<br><br>'
      + '<b>Units without ' + amenityDisplayName + '</b>'
      + '<small style="padding:5px; color:navy;"><b>(' + unitsWithoutAmenity.length + ' total)</b></small><br>'
      + unitsWithoutAmenity;

    return amenityTooltip;
  }
}
